import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { CategoryTag } from "../../shared/components/CategoryTag";
import { GridButtons } from "../components/others/GridButtons";
import { DelayByShift } from "../models/DelayByShift";

type Props = {
	justifyData: (delay: DelayByShift) => void;
	splitData: (delay: DelayByShift) => void;
	showContextMenu: Function;
};

// Format duration as HH:mm:ss like the OLD system
const formatDuration = (seconds: number | null | undefined): string => {
	if (!seconds || seconds <= 0) return "00:00:00";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = seconds % 60;

	const hh = String(hours).padStart(2, "0");
	const mm = String(minutes).padStart(2, "0");
	const ss = String(secs).padStart(2, "0");

	return `${hh}:${mm}:${ss}`;
};

// Get hierarchy (path without the last element, separated by " > ")
const getHierarchy = (delayCodePath: string | null | undefined): string => {
	if (!delayCodePath) return "";

	const parts = delayCodePath.split("/");
	if (parts.length <= 1) return "";

	// Remove the last element (that's the Reason) and join with " > "
	return parts.slice(0, -1).join(" > ");
};

export const useTableData = ({
	justifyData,
	splitData,
	showContextMenu,
}: Props) => {
	// Column order matches OLD system: Start, End, Duration, Reason, Hierarchy, Category, Comments, Product, Crew, Shift, Actions
	const columnDefs: ColDef[] = [
		{
			field: "Start",
			headerName: "Start",
			valueFormatter: (params) =>
				params.value
					? dxtToLocalServerTime(params.value, "yyyy-MM-dd HH:mm:ss")
					: "",
			width: 145,
			maxWidth: 145,
			sortable: true,
			sort: "desc",
		},
		{
			field: "End",
			headerName: "End",
			valueFormatter: (params) =>
				params.value
					? dxtToLocalServerTime(params.value, "yyyy-MM-dd HH:mm:ss")
					: "",
			width: 145,
			maxWidth: 145,
			sortable: true,
		},
		{
			field: "Duration",
			headerName: "Duration",
			valueFormatter: (params) => formatDuration(params.value),
			width: 75,
			maxWidth: 75,
			sortable: true,
		},
		{
			field: "DelayCodeName",
			headerName: "Reason",
			flex: 1.2,
			minWidth: 100,
			sortable: true,
		},
		{
			field: "DelayCodePath",
			headerName: "Hierarchy",
			valueGetter: (params) => {
				const delayCodePath = params.data?.DelayCodePath;
				const delayCodeName = params.data?.DelayCodeName;

				// If it's an undeclared system code, show empty
				const isSystemDelayCode =
					delayCodeName === "Undeclared Mill" ||
					delayCodeName === "Undeclared Cold Shear";

				if (isSystemDelayCode) return "";

				return getHierarchy(delayCodePath);
			},
			flex: 2,
			minWidth: 120,
		},
		{
			field: "DelayCategory",
			headerName: "Category",
			valueGetter: (params) => {
				const delayCategoryName = params.data?.DelayCategoryName;
				const delayCategoryColor = params.data?.DelayCategoryColor;
				return { name: delayCategoryName, color: delayCategoryColor };
			},
			cellRenderer: CategoryTag,
			width: 75,
			maxWidth: 75,
			sortable: true,
		},
		{
			field: "Comments",
			headerName: "Comments",
			flex: 2,
			minWidth: 150,
			wrapText: true,
			autoHeight: true,
		},
		{
			field: "ProductName",
			headerName: "Product",
			flex: 1.5,
			minWidth: 120,
			sortable: true,
		},
		{
			field: "Crew",
			headerName: "Crew",
			width: 55,
			maxWidth: 55,
			sortable: true,
		},
		{
			field: "Shift",
			headerName: "Shift",
			width: 50,
			maxWidth: 50,
			sortable: true,
		},
		{
			field: "",
			headerName: "",
			cellRenderer: GridButtons,
			cellRendererParams: (params: ICellRendererParams) => ({
				data: params.data,
				justifyData: justifyData,
				splitData: splitData,
				showContextMenu: () =>
					showContextMenu(event, params.data, "delayManagerContext"),
			}),
			wrapText: false,
			wrapHeaderText: false,
			sortable: false,
			flex: 1,
			maxWidth: 180,
			minWidth: 180,
			filter: false,
		},
	];
	return { columnDefs };
};
